import { NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatError } from '@angular/material/form-field';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButtonLoading } from '@ng-matero/extensions/button';
import { TranslatePipe } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

import { AuthService } from '@core/authentication';

import { AuthRedirectService } from '@core/authentication/auth-redirect.service';
import { NoticeService } from '@shared/services/notice/notice.service';
import { StringUtils } from '@shared/utils/stringUtils';

import { SsoComponent } from './sso/sso.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    NgIf,
    ReactiveFormsModule,
    MatError,
    RouterLink,
    MatButton,
    MatButtonLoading,
    SsoComponent,
    TranslatePipe,
  ],
})
export class LoginComponent {
  protected readonly authLogo = StringUtils.brandResourceWithFallback('sidebarLogo');
  public isSubmitting = false;

  public loginForm = this.fb.nonNullable.group({
    username: ['', [Validators.required]],
    password: ['', [Validators.required]],
    rememberMe: [true],
  });
  public twoFactorAuthForm = this.fb.nonNullable.group({
    token: ['', [Validators.required, Validators.maxLength(6), Validators.maxLength(6)]],
  });
  token2faError: string | undefined;
  is2FaRequired = false;
  credentialsError: string | undefined;

  protected redirectUrl: string | null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthService,
    private noticeService: NoticeService,
    private authRedirectService: AuthRedirectService
  ) {
    this.redirectUrl = this.authRedirectService.sanitize(
      this.route.snapshot.queryParamMap.get('redirectUrl')
    );
  }

  get username() {
    return this.loginForm.get('username')!;
  }

  get password() {
    return this.loginForm.get('password')!;
  }

  get rememberMe() {
    return this.loginForm.get('rememberMe')!;
  }

  login() {
    this.isSubmitting = true;
    this.credentialsError = undefined;
    this.auth
      .login(this.username.value, this.password.value, this.rememberMe.value)
      .pipe(
        filter((result: any) => {
          if (result.required2Fa) {
            this.is2FaRequired = true;
            this.isSubmitting = false;
          } else {
            return result.authenticated;
          }
        })
      )
      .subscribe(
        () => {
          this.noticeService.showNoticeAfterLogin();
          this.authRedirectService.clear();
          this.router.navigateByUrl(this.redirectUrl || '/');
        },
        (errorRes: HttpErrorResponse) => {
          if (errorRes.status === 400 || errorRes.status === 401) {
            this.credentialsError = 'Incorrect credentials';
          } else if ([0, 502, 503, 504].includes(errorRes.status)) {
            this.credentialsError =
              'The server is temporarily unavailable. Please wait a moment and try again.';
          } else {
            this.credentialsError = 'Unable to sign in. Please try again.';
          }
          this.isSubmitting = false;
        }
      );
  }

  verify2Fa() {
    this.token2faError = undefined;
    this.isSubmitting = true;
    this.auth.verify2FaCode(this.twoFactorAuthForm.controls.token.value).subscribe({
      error: (err: HttpErrorResponse) => {
        this.token2faError = [0, 502, 503, 504].includes(err.status)
          ? 'The server is temporarily unavailable. Please wait a moment and try again.'
          : err.error?.message || 'Unable to verify the code. Please try again.';
        this.isSubmitting = false;
      },
      next: result => {
        this.auth.setToken(result.token);
        this.noticeService.showNoticeAfterLogin();
        this.authRedirectService.clear();
        this.router.navigateByUrl(this.redirectUrl || '/');
      },
      complete: () => {
        this.isSubmitting = false;
      },
    });
  }
}
